import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { Spinner } from "reactstrap";
import styles from "../css/crfassignment.module.scss";
import CommonSearch from "Components/Common/CommonSearch";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonButton from "Components/Common/CommonButton";
import DeleteModal from "Components/Common/DeleteModal";
import { BreadcrumbContext } from "Layouts/Header";
import {
  CHECKLIST_CATEGORY_OPTIONS,
  loadCodelistsDetailId,
} from "api/modules/common.option";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";
import { formatDate, perPageData, showToastMessage } from "common/service/CommonService";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { useNavigate, useLocation } from "react-router-dom";
import { crfModuleColumns, getAssignCRFColumns } from "modules/Library/CRF/helpers/crfconstant";
import AssignCRFCategoryModal from "modules/Library/CRF/components/AssignCRFCategoryModal";
import { queryClient } from "api/queryClient";

interface CRFAssignmentProps {
  searchText?: string;
  onSearch?: (text: string | null) => void;
  onModuleSelect?: (module: any | null) => void;
  crfcategory?: number | string | null;
}

const CRFAssignment: React.FC<CRFAssignmentProps> = ({
  searchText: externalSearchText,
  onSearch: externalOnSearch,
  onModuleSelect,
  crfcategory,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Selected Module State ───────────────────────────────── */
  const initialModule = (location.state as any)?.selectedModule || null;
  const [selectedModule, setSelectedModule] = useState<any | null>(initialModule);

  useEffect(() => {
    if ((location.state as any)?.selectedModule) {
      setSelectedModule((location.state as any).selectedModule);
    }
  }, [location.state]);

  useEffect(() => {
    onModuleSelect?.(selectedModule);
  }, [selectedModule, onModuleSelect]);

  const onView = useCallback((item: any): void => {
    localStorage.setItem("currentCRFType", "masterCRF");
    const crfId = item?.crfid ?? item?.id;
    navigate(`/crf/view/${crfId}`, {
      state: {
        ...item,
        moduleName: selectedModule?.label || selectedModule?.name || "",
        selectedModule: selectedModule,
      },
    });
  }, [navigate, selectedModule]);
  /* ── Module List State ───────────────────────────────────── */
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [moduleSearchText, setModuleSearchText] = useState<string>("");
  const [modulePage, setModulePage] = useState(1);

  /* ── Breadcrumb Context ─────────────────────────────────── */
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    if (selectedModule) {
      setBreadcrumbItems?.([
        {
          text: "CRF Assignment",
          onclick: () => {
            setSelectedModule(null);
            setSelectedCRFIdsToAssign([]);
            setCrfSearchText("");
            setCrfPage(1);
            if (externalOnSearch) externalOnSearch("");
          },
        },
        {
          text: selectedModule.label,
        },
      ]);
    } else {
      setBreadcrumbItems?.([]);
    }

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, [selectedModule, setBreadcrumbItems, externalOnSearch]);

  /* ── Assignment Sub-Tabs State ───────────────────────────── */
  const [activeSubTab, setActiveSubTab] = useState<"all" | "assigned">("all");
  const [crfSearchText, setCrfSearchText] = useState<string>("");
  const [crfPage, setCrfPage] = useState(1);
  const [selectedCRFIdsToAssign, setSelectedCRFIdsToAssign] = useState<(number | string)[]>([]);

  /* ── Delete Unassign Modal State ─────────────────────────── */
  const [deleteModal, setDeleteModal] = useState(false);
  const [recordToUnassign, setRecordToUnassign] = useState<any | null>(null);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const ToggleDeleteResason = (): void => setShowDeleteReason((prev) => !prev);

  /* ── Assign Category State ───────────────────────────────── */
  const [assignCategoryModalOpen, setAssignCategoryModalOpen] = useState(false);
  const [selectedModuleForCategory, setSelectedModuleForCategory] = useState<any>(null);
  const [selectedCategoryValue, setSelectedCategoryValue] = useState<any>(null);

  /* ── Service Queries & Mutations ─────────────────────────── */
  const effectiveSearch = externalSearchText !== undefined ? externalSearchText : moduleSearchText;

  // Query 1: For the assigned CRFs of a selected module
  const { AssignCRFSelectAllQuery, AssignCRFInsertMutation, AssignCRFDeleteMutation } = useCRFService({
    moduleid: selectedModule?.value,
    categorynames: null,
    searchText: crfSearchText,
    pageNumber: crfPage,
    pageSize: perPageData,
    enabledSelectAll: false,
    enabledCategorySelectAll: false,
  });

  // Query 2: For the module category assignment list (Screen 1)
  const { CategorySelectAllQuery, UpdateCRFCategoryMutation } = useCRFService({
    searchText: effectiveSearch,
    pageNumber: modulePage,
    pageSize: perPageData,
    crfcategory: crfcategory,
    enabledSelectAll: false,
    enabledCategorySelectAll: !selectedModule,
  });

  const assignmentData = useMemo(() => {
    const raw = (AssignCRFSelectAllQuery.data as any)?.data;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw[0];
    }
    if (raw && typeof raw === "object") {
      return raw;
    }
    return {};
  }, [AssignCRFSelectAllQuery.data]);

  const assignedCRFs = useMemo(() => {
    return Array.isArray(assignmentData?.assigned) ? assignmentData.assigned : [];
  }, [assignmentData]);

  const unassignedCRFs = useMemo(() => {
    return Array.isArray(assignmentData?.remaining) ? assignmentData.remaining : [];
  }, [assignmentData]);

  const displayedCRFData = useMemo(() => {
    return activeSubTab === "all" ? unassignedCRFs : assignedCRFs;
  }, [activeSubTab, unassignedCRFs, assignedCRFs]);

  const totalCRFCount = useMemo(() => {
    if (activeSubTab === "all") {
      return typeof assignmentData?.remaining_total_count === "number"
        ? assignmentData.remaining_total_count
        : unassignedCRFs.length;
    } else {
      return typeof assignmentData?.assigned_total_count === "number"
        ? assignmentData.assigned_total_count
        : assignedCRFs.length;
    }
  }, [activeSubTab, assignmentData, unassignedCRFs, assignedCRFs]);

  /* ── Reset page numbers on search change ─────────────────── */
  useEffect(() => {
    setCrfPage(1);
    setModulePage(1);
  }, [effectiveSearch, crfSearchText]);

  /* ── Auto-adjust page numbers when total count decreases ── */
  useEffect(() => {
    if (!AssignCRFSelectAllQuery.isFetching && AssignCRFSelectAllQuery.isSuccess && totalCRFCount > 0) {
      const maxPages = Math.max(1, Math.ceil(totalCRFCount / perPageData));
      if (crfPage > maxPages) {
        setCrfPage(maxPages);
      }
    }
  }, [totalCRFCount, crfPage, AssignCRFSelectAllQuery.isFetching, AssignCRFSelectAllQuery.isSuccess]);

  /* ── Load Codelist Options ───────────────────────────────── */
  useEffect(() => {
    loadCodelistsDetailId(["CHECKLIST_CATEGORY_OPTIONS"])
      .then(() => {
        setOptionsLoaded(true);
      })
      .catch(() => {
        setOptionsLoaded(true);
      });
  }, []);

  const paginatedModules = useMemo(() => {
    const raw = (CategorySelectAllQuery.data as any)?.data;
    if (Array.isArray(raw)) {
      return raw.map((item: any) => ({
        ...item,
        label: item.modulename,
        value: item.moduleid,
      }));
    }
    return [];
  }, [CategorySelectAllQuery.data]);

  const totalModuleCount = useMemo(() => {
    const raw = (CategorySelectAllQuery.data as any)?.data;
    return raw?.[0]?.total_count || 0;
  }, [CategorySelectAllQuery.data]);

  useEffect(() => {
    const maxPages = Math.max(1, Math.ceil(totalModuleCount / perPageData));
    if (modulePage > maxPages) {
      setModulePage(maxPages);
    }
  }, [totalModuleCount, modulePage]);

  /* ── Handlers ────────────────────────────────────────────── */
  const handleModuleClick = (row: any) => {
    setSelectedModule({
      value: row.moduleid,
      label: row.modulename,
      ...row
    });
    setActiveSubTab("all");
    setSelectedCRFIdsToAssign([]);
    setCrfSearchText("");
    setCrfPage(1);
    if (externalOnSearch) {
      externalOnSearch("");
    }
  };

  const getAssignedCategoryForModule = (row: any) => {
    return row?.categoryid ?? row?.crfcategory ?? row?.categoryvalue ?? null;
  };

  const handleAssignCategory = (row: any, existingCat: any = null) => {
    setSelectedModuleForCategory({
      value: row.moduleid,
      label: row.modulename,
      ...row
    });
    setSelectedCategoryValue(existingCat);
    setAssignCategoryModalOpen(true);
  };

  const onSaveCategory = (payload: any) => {
    UpdateCRFCategoryMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Category assigned successfully");
        queryClient.invalidateQueries({ queryKey: ["crf-category-selectAll"] });
        setAssignCategoryModalOpen(false);
        setSelectedModuleForCategory(null);
        setSelectedCategoryValue(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error ?? "Failed to assign category");
      },
    });
  };

  const toggleCRFSelection = (row: any) => {
    const id = row.crfid ?? row.id;
    setSelectedCRFIdsToAssign((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSaveAssignments = async () => {
    if (!selectedModule || selectedCRFIdsToAssign.length === 0) return;
    setIsSaving(true);

    AssignCRFInsertMutation.mutate({
      moduleid: selectedModule.value,
      crfids: selectedCRFIdsToAssign,
    }, {
      onSuccess: (res:any) => {
        showToastMessage("success",res?.message);
        setSelectedCRFIdsToAssign([]);
        AssignCRFSelectAllQuery.refetch();
      },
      onError: (error: any) => {
        showToastMessage("error",error);
      },
      onSettled: () => {
        setIsSaving(false);
      },
    })
  };

  const handleUnassignClick = (row: any) => {
    setRecordToUnassign(row);
    setDeleteModal(true);
  };

  const confirmUnassign = (reasonPayload: {
    id: number | null;
    reason: string;
  }): void => {
    if (!recordToUnassign) return;
    const idToUnassign = reasonPayload.id ?? recordToUnassign.assignmentid;

    AssignCRFDeleteMutation.mutate(
      {
        assignmentid: idToUnassign,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage("success", res?.message);
          AssignCRFSelectAllQuery.refetch();
        },
        onError: (error: any) => {
          showToastMessage("error", error);
        },
        onSettled: () => {
          setDeleteModal(false);
          setRecordToUnassign(null);
          setShowDeleteReason(false);
        },
      }
    );
  };

  const toggleAllPage = useCallback((selectAll: boolean) => {
    const pageIds = displayedCRFData.map((row: any) => row.crfid ?? row.id);
    setSelectedCRFIdsToAssign((prev) => {
      if (selectAll) {
        const set = new Set([...prev, ...pageIds]);
        return Array.from(set);
      } else {
        return prev.filter((id) => !pageIds.includes(id));
      }
    });
  }, [displayedCRFData]);

  const crfColumns = useMemo(() => {
    return getAssignCRFColumns(
      selectedCRFIdsToAssign,
      toggleCRFSelection,
      activeSubTab,
      onView,
      displayedCRFData,
      toggleAllPage,
    );
  }, [selectedCRFIdsToAssign, activeSubTab, onView, displayedCRFData, toggleAllPage]);

  /* ────────────────────────────────────────────────────────── */
  /*  RENDER SCREEN 1: MODULE SELECTION LIST                     */
  /* ────────────────────────────────────────────────────────── */
  if (!selectedModule) {
    return (
      <div>
        {externalSearchText === undefined && (
          <div className="d-flex align-items-center justify-content-between mb-3">
            <CommonSearch
              onSearch={(val) => {
                setModuleSearchText(val || "");
                setModulePage(1);
              }}
              searchText={moduleSearchText}
              placeholder="Search module name, created on..."
            />
          </div>
        )}

        <CommonTableIndexWise
          moduleName={PERMISSION_MODULES.CRF}
          columns={crfModuleColumns(handleModuleClick)}
          data={paginatedModules}
          loading={!optionsLoaded || CategorySelectAllQuery.isLoading}
          pageSize={perPageData}
          currentPage={modulePage}
          onPageChange={setModulePage}
          totalRecords={totalModuleCount}
          actions={(row: any) => {
            const existingCat = getAssignedCategoryForModule(row);
            if (existingCat !== null && existingCat !== undefined && existingCat !== "") {
              return {
                onUpdateCategory: (r: any) => handleAssignCategory(r, existingCat),
              };
            }
            return {
              onAssignCategory: (r: any) => handleAssignCategory(r, null),
            };
          }}
        />

        {/* Assign Category Modal */}
        <AssignCRFCategoryModal
          isOpen={assignCategoryModalOpen}
          toggle={() => setAssignCategoryModalOpen(!assignCategoryModalOpen)}
          selectedModule={selectedModuleForCategory}
          existingCategory={selectedCategoryValue}
          onSave={onSaveCategory}
          isLoading={UpdateCRFCategoryMutation.isPending}
        />
      </div>
    );
  }
  /* ────────────────────────────────────────────────────────── */
  /*  RENDER SCREEN 2: MODULE CRF ASSIGNMENT (Sub-tabs)        */
  /* ────────────────────────────────────────────────────────── */
  return (
    <div>
      {/* Main Assignment Card */}
      <div className={styles.mainCard}>
        {/* Navigation Sub-Tabs matching VolunteerList.tsx */}
        <div className="px-3 py-2 border-bottom bg-light bg-opacity-10 d-flex align-items-center justify-content-between">
          <div
            className="d-flex align-items-center gap-4 px-1"
            style={{
              borderBottom: "1px solid #eff2f7",
              marginBottom: "-1px"
            }}
          >
            <div
              className="position-relative cursor-pointer pb-2"
              style={{
                transition: "all 0.2s ease",
                borderBottom: activeSubTab === "all" ? "2px solid #1e6b91" : "2px solid transparent"
              }}
              onClick={() => {
                setActiveSubTab("all");
                setCrfSearchText("");
                setCrfPage(1);
              }}
            >
              <div className="d-flex align-items-center gap-2 px-1">
                <i className={`ri-file-list-3-line ${activeSubTab === "all" ? "text-primary" : "text-muted"}`} style={{ fontSize: "15px" }} />
                <span
                  className={`fw-semibold ${activeSubTab === "all"
                    ? "text-primary"
                    : "text-muted"
                    }`}
                  style={{
                    fontSize: "12px",
                    letterSpacing: "0.2px"
                  }}
                >
                  All CRF ({assignmentData?.remaining_total_count ?? unassignedCRFs.length})
                </span>
              </div>
            </div>

            <div
              className="position-relative cursor-pointer pb-2"
              style={{
                transition: "all 0.2s ease",
                borderBottom: activeSubTab === "assigned" ? "2px solid #1e6b91" : "2px solid transparent"
              }}
              onClick={() => {
                setActiveSubTab("assigned");
                setCrfSearchText("");
                setCrfPage(1);
              }}
            >
              <div className="d-flex align-items-center gap-2 px-1">
                <i className={`ri-checkbox-circle-line ${activeSubTab === "assigned" ? "text-primary" : "text-muted"}`} style={{ fontSize: "15px" }} />
                <span
                  className={`fw-semibold ${activeSubTab === "assigned"
                    ? "text-primary"
                    : "text-muted"
                    }`}
                  style={{
                    fontSize: "12px",
                    letterSpacing: "0.2px"
                  }}
                >
                  Assigned CRFs ({assignmentData?.assigned_total_count ?? assignedCRFs.length})
                </span>
              </div>
            </div>
          </div>

          {externalSearchText === undefined && (
            <div>
              <CommonSearch
                onSearch={(val) => {
                  setCrfSearchText(val || "");
                  setCrfPage(1);
                }}
                searchText={crfSearchText}
                placeholder="Search CRF name..."
              />
            </div>
          )}
        </div>

        {/* CRF Table Content */}
        <div key={activeSubTab} className={`px-3 mb-2 ${styles.tabContentArea}`}>
          <CommonTableIndexWise
            moduleName={PERMISSION_MODULES.CRF}
            columns={crfColumns}
            data={displayedCRFData}
            loading={AssignCRFSelectAllQuery.isLoading || AssignCRFSelectAllQuery.isFetching}
            pageSize={perPageData}
            currentPage={crfPage}
            onPageChange={setCrfPage}
            totalRecords={totalCRFCount}
            hideAction={activeSubTab !== "assigned"}
            actions={{
              onDelete: handleUnassignClick,
            }}
          />
        </div>

        {/* Sticky Footer Bar */}
        {activeSubTab === "all" && (
          <div className={styles.stickyFooter}>
            <div className={styles.selectionCount}>
              {selectedCRFIdsToAssign.length > 0 ? (
                <span>
                  {selectedCRFIdsToAssign.length} CRF
                  {selectedCRFIdsToAssign.length > 1 ? "s" : ""} selected for assignment
                </span>
              ) : (
                <span className="text-muted fw-normal">Select CRFs using checkboxes above</span>
              )}
            </div>
            <div className={styles.footerActions}>
              <CommonButton
                color="dark"
                outline
                onClick={() => setSelectedCRFIdsToAssign([])}
                disabled={selectedCRFIdsToAssign.length === 0}
              >
                Clear Selection
              </CommonButton>
              <CommonButton
                color="primary"
                outline
                disabled={selectedCRFIdsToAssign.length === 0 || isSaving}
                onClick={handleSaveAssignments}
              >
                {isSaving ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-save-line me-1" />
                )}
                Save Assignment
              </CommonButton>
            </div>
          </div>
        )}
      </div>

      {/* Delete / Unassign Confirmation Modal */}
      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={() => {
            setDeleteModal(false);
            setShowDeleteReason(false);
            setRecordToUnassign(null);
          }}
          recordId={recordToUnassign?.assignmentid}
          recordName={recordToUnassign?.name || recordToUnassign?.crfName}
          onDeleteClick={confirmUnassign}
          isLoading={AssignCRFDeleteMutation.isPending}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={ToggleDeleteResason}
        />
      )}
    </div>
  );
};

export default CRFAssignment;
